import React, { useState } from "react";
import {
  Card,
  Steps,
  Form,
  Input,
  DatePicker,
  Button,
  Space,
  Typography,
  message,
  Divider,
  List,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  GiftFilled,
  GiftOutlined,
  SendOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import dayjs, { Dayjs } from "dayjs";
import { createEvent } from "../../services/api";

const { TextArea } = Input;
const { Title, Paragraph, Text } = Typography;

interface FormValues {
  name: string;
  description?: string;
  rules: string;
  deadline: Dayjs;
  organizerEmail: string;
  participants: { name: string; email: string }[];
}

const STEP_FIELDS: (keyof FormValues)[][] = [
  ["name", "description"],
  ["rules", "deadline"],
  ["participants"],
  ["organizerEmail"],
];

export default function CreateEventPage() {
  const [form] = Form.useForm<FormValues>();
  const [currentStep, setCurrentStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const goNext = async () => {
    try {
      await form.validateFields(STEP_FIELDS[currentStep] as string[]);
      setCurrentStep((s) => s + 1);
    } catch {
      // lỗi validate đã hiển thị trên form
    }
  };

  const goBack = () => setCurrentStep((s) => s - 1);

  const handleFinish = async (values: FormValues) => {
    setSubmitting(true);
    try {
      const result = await createEvent({
        name: values.name,
        description: values.description,
        rules: values.rules,
        deadline: values.deadline.toISOString(),
        organizerEmail: values.organizerEmail,
        participants: values.participants,
      });
      navigate("/success", { state: result });
    } catch (err) {
      message.error(
        err instanceof Error
          ? err.message
          : "Có lỗi xảy ra. Vui lòng thử lại nhé!",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="page-container">
      <Card className="page-card">
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div
            style={{
              display: "inline-block",
              padding: "16px",
              background: "#fff0f0",
              borderRadius: "50%",
              marginBottom: 16,
            }}
          >
            <GiftFilled style={{ fontSize: 36, color: "#ff5a5f" }} />
          </div>
          <Title level={2} style={{ margin: 0, fontWeight: 700 }}>
            Tạo Sự Kiện Đổi Quà{" "}
            <GiftOutlined style={{ color: "#ff5a5f", marginLeft: 4 }} />
          </Title>
          <Paragraph type="secondary" style={{ fontSize: 16, marginTop: 8 }}>
            Chỉ vài bước đơn giản, hệ thống sẽ giúp bạn xáo trộn và gửi thiệp
            mời bí mật đến mọi người!
          </Paragraph>
        </div>

        <Steps
          current={currentStep}
          size="small"
          items={[
            { title: "Sự kiện" },
            { title: "Luật chơi" },
            { title: "Người chơi" },
            { title: "Xác nhận" },
          ]}
          style={{ marginBottom: 40 }}
        />

        <Form
          form={form}
          layout="vertical"
          initialValues={{
            participants: [
              { name: "", email: "" },
              { name: "", email: "" },
              { name: "", email: "" },
            ],
          }}
          onFinish={handleFinish}
          requiredMark={false}
        >
          <div
            style={{
              display: currentStep === 0 ? "block" : "none",
              animation: "fadeIn 0.5s",
            }}
          >
            <Form.Item
              label={<Text strong>Tên sự kiện của bạn là gì?</Text>}
              name="name"
              rules={[
                {
                  required: true,
                  message: "Đừng quên đặt tên cho sự kiện nhé!",
                },
              ]}
            >
              <Input size="large" placeholder="Ví dụ: Giáng sinh ấm áp 2026" />
            </Form.Item>
            <Form.Item
              label={<Text strong>Lời nhắn gửi (Không bắt buộc)</Text>}
              name="description"
            >
              <TextArea
                rows={4}
                placeholder="Vài lời nhắn nhủ dễ thương để hâm nóng bầu không khí..."
              />
            </Form.Item>
          </div>

          <div
            style={{
              display: currentStep === 1 ? "block" : "none",
              animation: "fadeIn 0.5s",
            }}
          >
            <Form.Item
              label={<Text strong>Luật chơi & Ngân sách</Text>}
              name="rules"
              rules={[
                {
                  required: true,
                  message: "Hãy ghi rõ luật chơi để mọi người cùng biết nhé",
                },
              ]}
            >
              <TextArea
                rows={4}
                placeholder="Ví dụ: Quà trị giá khoảng 200k, ưu tiên đồ thủ công, mang tới văn phòng vào sáng thứ 6..."
              />
            </Form.Item>
            <Form.Item
              label={<Text strong>Hạn chót điền điều ước (Wishlist)</Text>}
              name="deadline"
              rules={[
                { required: true, message: "Vui lòng chọn thời gian chốt sổ" },
              ]}
            >
              <DatePicker
                showTime
                size="large"
                style={{ width: "100%" }}
                format="DD/MM/YYYY HH:mm"
                placeholder="Chọn ngày và giờ"
                disabledDate={(d) => d && d < dayjs().startOf("day")}
              />
            </Form.Item>
          </div>

          <div
            style={{
              display: currentStep === 2 ? "block" : "none",
              animation: "fadeIn 0.5s",
            }}
          >
            <Form.List
              name="participants"
              rules={[
                {
                  validator: async (_, participants) => {
                    // 1. Kiểm tra số lượng người tham gia tối thiểu
                    if (!participants || participants.length < 3) {
                      return Promise.reject(
                        new Error(
                          "Càng đông càng vui! Cần ít nhất 3 người tham gia.",
                        ),
                      );
                    }

                    // 2. Kiểm tra xem có người tham gia nào bị trống tên hoặc email không
                    const isAnyFieldEmpty = participants.some(
                      (p: { name: string; email: string }) =>
                        !p || !p.name || !p.email,
                    );

                    if (isAnyFieldEmpty) {
                      return Promise.reject(
                        new Error(
                          "Vui lòng điền đầy đủ tên và email cho tất cả người tham gia.",
                        ),
                      );
                    }

                    // 3. Kiểm tra định dạng email hợp lệ
                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                    const isAnyEmailInvalid = participants.some(
                      (p: { name: string; email: string }) =>
                        !emailRegex.test(p.email),
                    );

                    if (isAnyEmailInvalid) {
                      return Promise.reject(
                        new Error(
                          "Có địa chỉ email chưa hợp lệ. Vui lòng kiểm tra lại.",
                        ),
                      );
                    }
                  },
                },
              ]}
            >
              {(fields, { add, remove }, { errors }) => (
                <>
                  <div
                    style={{
                      background: "#f8f9fa",
                      padding: 16,
                      borderRadius: 16,
                      marginBottom: 16,
                    }}
                  >
                    {fields.map((field) => (
                      <Space
                        key={field.key}
                        align="baseline"
                        style={{
                          display: "flex",
                          width: "100%",
                          marginBottom: 12,
                        }}
                      >
                        <Form.Item
                          {...field}
                          name={[field.name, "name"]}
                          rules={[{ required: true, message: "Nhập tên" }]}
                          style={{ margin: 0, flex: 1 }}
                        >
                          <Input
                            size="large"
                            placeholder="Tên người tham gia"
                          />
                        </Form.Item>
                        <Form.Item
                          {...field}
                          name={[field.name, "email"]}
                          rules={[
                            { required: true, message: "Nhập email" },
                            { type: "email", message: "Email chưa hợp lệ" },
                          ]}
                          style={{ margin: 0, flex: 2 }}
                        >
                          <Input size="large" placeholder="Email nhận thiệp" />
                        </Form.Item>
                        {fields.length > 3 && (
                          <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => remove(field.name)}
                          />
                        )}
                      </Space>
                    ))}
                  </div>
                  <Form.Item>
                    <Button
                      type="dashed"
                      size="large"
                      onClick={() => add()}
                      icon={<PlusOutlined />}
                      style={{ width: "100%", borderRadius: 12 }}
                    >
                      Thêm người tham gia
                    </Button>
                    <Form.ErrorList errors={errors} />
                  </Form.Item>
                </>
              )}
            </Form.List>
          </div>

          <div
            style={{
              display: currentStep === 3 ? "block" : "none",
              animation: "fadeIn 0.5s",
            }}
          >
            <Form.Item
              label={<Text strong>Email của bạn (Người tổ chức)</Text>}
              name="organizerEmail"
              rules={[
                { required: true, message: "Vui lòng để lại email của bạn" },
                { type: "email", message: "Email chưa hợp lệ" },
              ]}
            >
              <Input size="large" placeholder="de-thuong@example.com" />
            </Form.Item>

            <Divider style={{ borderColor: "#ffe4e1" }} />
            <div style={{ textAlign: "center", marginBottom: 16 }}>
              <Title level={4} style={{ color: "#ff5a5f" }}>
                Kiểm tra lại thông tin nhé!
              </Title>
            </div>
            <ReviewSummary form={form} />
          </div>

          <Space
            style={{
              width: "100%",
              justifyContent: "space-between",
              marginTop: 32,
            }}
          >
            {currentStep > 0 ? (
              <Button
                size="large"
                onClick={goBack}
                style={{ borderRadius: 100, padding: "0 32px" }}
              >
                Quay lại
              </Button>
            ) : (
              <div />
            )}
            {currentStep < 3 && (
              <Button
                type="primary"
                size="large"
                onClick={goNext}
                style={{ borderRadius: 100, padding: "0 32px" }}
              >
                Tiếp tục
              </Button>
            )}
            {currentStep === 3 && (
              <Button
                type="primary"
                size="large"
                htmlType="submit"
                loading={submitting}
                icon={<SendOutlined />}
                style={{ borderRadius: 100, padding: "0 32px" }}
              >
                Xáo Trộn & Gửi Thiệp Mời
              </Button>
            )}
          </Space>
        </Form>
      </Card>
    </div>
  );
}

function ReviewSummary({
  form,
}: {
  form: ReturnType<typeof Form.useForm<FormValues>>[0];
}) {
  const values = form.getFieldsValue(true) as Partial<FormValues>;
  const participants = values.participants || [];

  return (
    <List
      size="large"
      style={{
        background: "#fff9f9",
        borderRadius: 16,
        border: "1px solid #ffe4e1",
      }}
      dataSource={[
        { label: "Tên sự kiện", value: values.name || "—" },
        {
          label: "Hạn chốt sổ",
          value: values.deadline
            ? dayjs(values.deadline).format("HH:mm - DD/MM/YYYY")
            : "—",
        },
        {
          label: "Số lượng tham gia",
          value: `${participants.length} người chơi`,
        },
      ]}
      renderItem={(item) => (
        <List.Item
          style={{ borderBottom: "1px solid #ffe4e1", padding: "16px 24px" }}
        >
          <Text type="secondary">{item.label}</Text>
          <Text strong style={{ fontSize: 16 }}>
            {item.value}
          </Text>
        </List.Item>
      )}
    />
  );
}
